import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Calendar, PlusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import AppShell from "@/components/AppShell";
import TimelineSection from "@/components/TimelineSection";
import { TimelineSection as TimelineSectionType, TimelineEntry } from "@/types";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useTimelineAccess } from "@/hooks/useTimelineAccess";

import TimelineAccessDenied from "@/components/timeline/TimelineAccessDenied";
import TimelineChatSidebar from "@/components/timeline/TimelineChatSidebar";
import TimelineNavigation from "@/components/timeline/TimelineNavigation";
import TimelineSwitcher from "@/components/timeline/TimelineSwitcher";
import { useMobileDetect } from "@/hooks/useMobileDetect";
import { useUserSearch } from "@/hooks/useUserSearch";
import { useTimelineInitialization } from "@/hooks/useTimelineInitialization";
import { useZodiacSign, getZodiacDisplayName } from "@/hooks/useZodiacSign";
import { useTimelineEntryReassignment } from "@/hooks/useTimelineEntryReassignment";
import { TimelineSetupCTA } from "@/components/timeline/TimelineSetupCTA";

const Timeline = () => {
  const [sections, setSections] = useState<TimelineSectionType[]>([]);
  const [expandSectionId, setExpandSectionId] = useState<string | null>(null);
  const [sectionsInitialized, setSectionsInitialized] = useState(false);
  const [isChatSidebarOpen, setIsChatSidebarOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<'create' | 'view'>('create');
  const [viewEntryId, setViewEntryId] = useState<string | null>(null);
  const [preselectedSectionId, setPreselectedSectionId] = useState<string | null>(null);
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const username = params.username; // This will be undefined for /timeline route
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sendFriendRequest } = useUserSearch();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation('timeline');
  const { isMobileOrTablet } = useMobileDetect();
  const { initializeTimeline, isInitializing } = useTimelineInitialization();
  const { reassignEntries, isReassigning } = useTimelineEntryReassignment();

  // Handle URL parameters for deep linking
  useEffect(() => {
    const entryId = searchParams.get('entry');
    const sectionId = searchParams.get('section');
    
    if (entryId) {
      // Open sidebar in view mode for the specific entry
      setViewEntryId(entryId);
      setSidebarMode('view');
      setIsChatSidebarOpen(true);
      
      // Also expand the section if provided
      if (sectionId) {
        setExpandSectionId(sectionId);
      }
    } else if (sectionId) {
      // Just expand the section
      setExpandSectionId(sectionId);
    }
  }, [searchParams]);

  // Auto-scroll to section when expanded
  useEffect(() => {
    if (expandSectionId && sectionsInitialized) {
      setTimeout(() => {
        const sectionElement = document.querySelector(`[data-section-id="${expandSectionId}"]`);
        if (sectionElement) {
          sectionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 500);
    }
  }, [expandSectionId, sectionsInitialized]);

  // Debug translation issues
  useEffect(() => {
    console.log('🐛 Timeline Translation Debug:', {
      currentLanguage: i18n.language,
      titleTranslation: t('title'),
      ofTranslation: t('of'),
      isI18nReady: i18n.isInitialized,
      loadedResources: i18n.hasResourceBundle(i18n.language, 'timeline'),
      allLoadedLanguages: Object.keys(i18n.store.data),
      timelineResources: i18n.store.data[i18n.language]?.timeline
    });
  }, [i18n.language, t]);

  // Use the updated useTimelineAccess hook
  const {
    profileData,
    timelineSections,
    isLoading,
    error,
    needsTimelineSetup,
    canView,
    isOwnProfile
  } = useTimelineAccess(username);

  const { data: zodiacData, isLoading: zodiacLoading, error: zodiacError } = useZodiacSign(profileData?.birthdate);


  // Reset sections state when username changes
  useEffect(() => {
    setSectionsInitialized(false);
    setSections([]);
    setExpandSectionId(null);
  }, [username]);

  // Fetch timeline entries for the user (only if authorized - RLS will handle this)
  const { data: entries } = useQuery({
    queryKey: ['timeline-entries', profileData?.id],
    queryFn: async () => {
      if (!profileData?.id || !canView) return [];
      
      const { data, error } = await supabase
        .from('profile_timeline_entries')
        .select('*')
        .eq('user_id', profileData.id)
        .order('date', { ascending: true });
      
      if (error) {
        console.log('Timeline entries query failed:', error);
        return [];
      }
      return data as TimelineEntry[];
    },
    enabled: !!profileData?.id && canView
  });

  // Manual reassignment trigger for debugging/testing
  const handleManualReassignment = async () => {
    if (profileData?.id && profileData?.birthdate) {
      await reassignEntries(profileData.id, profileData.birthdate);
    }
  };

  // Initialize sections with proper year ranges based on birthdate
  useEffect(() => {
    if (!isLoading && profileData?.birthdate && canView && !sectionsInitialized && timelineSections.length > 0) {
      console.log('🔧 Timeline - Initializing sections for profile:', profileData.id);
      const updatedSections = timelineSections.map(section => ({
        ...section,
        isExpanded: false
      }));
      setSections(updatedSections);
      setSectionsInitialized(true);
    }
  }, [isLoading, profileData, canView, timelineSections, sectionsInitialized]);

  useEffect(() => {
    if (expandSectionId && sectionsInitialized) {
      setSections(prevSections => 
        prevSections.map(section => 
          section.id === expandSectionId 
            ? { ...section, isExpanded: true }
            : { ...section, isExpanded: false }
        )
      );

      setTimeout(() => {
        const sectionElement = document.querySelector(`[data-section-id="${expandSectionId}"]`);
        if (sectionElement) {
          sectionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 500);
    }
  }, [expandSectionId, sectionsInitialized]);

  const toggleSectionExpand = (sectionId: string) => {
    setSections(prevSections => 
      prevSections.map(section => 
        section.id === sectionId 
          ? { ...section, isExpanded: !section.isExpanded }
          : { ...section, isExpanded: false }
      )
    );
    setExpandSectionId(null);
  };

  const handleMemoryCreated = async (sectionId: string) => {
    // Invalidate timeline entries to ensure immediate visibility
    await queryClient.invalidateQueries({ 
      queryKey: ['timeline-entries', profileData?.id],
      exact: false 
    });
    
    // Expand the section and close sidebar
    setExpandSectionId(sectionId);
    setIsChatSidebarOpen(false);
    
    // Scroll to the section after a brief delay for DOM updates
    setTimeout(() => {
      const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (sectionElement) {
        sectionElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 1000);
  };

  const handleCreateMemoryForSection = (sectionId: string) => {
    setSidebarMode('create');
    setViewEntryId(null);
    setPreselectedSectionId(sectionId);
    setIsChatSidebarOpen(true);
    // Clear URL parameters when creating
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('entry');
    newSearchParams.delete('section');
    setSearchParams(newSearchParams);
  };

  const handleCloseSidebar = () => {
    setIsChatSidebarOpen(false);
    setSidebarMode('create');
    setViewEntryId(null);
    setPreselectedSectionId(null);
    // Clear URL parameters when closing
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('entry');
    newSearchParams.delete('section');
    setSearchParams(newSearchParams);
  };

  const handleSendFriendRequest = async () => {
    if (profileData?.id) {
      await sendFriendRequest(profileData.id);
    }
  };

  const handleInitializeTimeline = () => {
    if (profileData?.id) {
      initializeTimeline(profileData.id);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center">{t('loading')}</div>
        </div>
      </AppShell>
    );
  }

  // Show error state for profile errors or access denied
  if (error) {
    if (error.includes('Access denied') || error.includes('not found') || !canView) {
      return (
        <AppShell 
          showHeader={false} 
          showNavigation={false}
          backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
        >
          <TimelineAccessDenied 
            username={username} 
            onSendFriendRequest={handleSendFriendRequest} 
            canSendRequest={!!username && !!profileData?.id} 
          />
        </AppShell>
      );
    }

    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <div className="text-red-500 mb-4">{error}</div>
            <Button onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  // Handle timeline setup needed (only for own profile)
  if (needsTimelineSetup && isOwnProfile) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center max-w-md">
            <div className="mb-6 w-16 h-16 rounded-full bg-uplivion-lightBlue flex items-center justify-center mx-auto">
              <Calendar className="h-8 w-8 text-uplivion-blue" />
            </div>
            <h1 className="text-2xl font-bold text-uplivion-blue mb-3">{t('createTimeline.title')}</h1>
            <p className="text-gray-600 mb-6">
              {t('createTimeline.description')}
            </p>
            <div className="space-y-4">
              <Button onClick={() => navigate("/create")} className="gap-2 bg-uplivion-blue hover:bg-uplivion-blue/90 w-full">
                <PlusCircle className="h-4 w-4" />
                {t('createTimeline.button')}
              </Button>
              <Button 
                onClick={handleInitializeTimeline} 
                disabled={isInitializing}
                variant="outline" 
                className="w-full"
              >
                {isInitializing ? 'Initializing...' : 'Initialize Timeline Now'}
              </Button>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // Define navigation elements early so they can be used in all states
  const timelineHeader = (
    <TimelineSwitcher currentUsername={username} />
  );

  const timelineFooter = (
    <TimelineNavigation 
      onMemoryCreated={handleMemoryCreated} 
      onOpenSidebar={() => setIsChatSidebarOpen(true)} 
      isVisible={true}
      targetUserId={profileData?.id}
      targetUserProfile={profileData}
      timelineSections={timelineSections}
      sectionsLoading={isLoading}
      timelineError={!!error}
      hasTimelineSetup={!!profileData?.birthdate}
    />
  );

  if (!profileData?.birthdate && isOwnProfile) {
    return (
      <AppShell 
        showHeader={true} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
        headerContent={timelineHeader}
      >
        {/* Main Timeline Content Container */}
        <div className="relative h-full">
          {/* Timeline Content */}
          <div className="h-full overflow-y-auto scrollbar-hide">
            <div className="py-8">
              <div className="container mx-auto">
                {/* Centered setup content within the timeline flow */}
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                  <div className="max-w-lg w-full">
                    <TimelineSetupCTA 
                      userId={profileData.id} 
                      onTimelineSetup={() => {
                        // Refresh the page to show the new timeline sections
                        queryClient.invalidateQueries({ queryKey: ['profile-timeline', profileData.id] });
                        window.location.reload();
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Timeline Footer Navigation */}
        <div className="fixed bottom-0 left-0 right-0 z-40">
            <TimelineNavigation 
              onMemoryCreated={handleMemoryCreated} 
              onOpenSidebar={() => {
                setSidebarMode('create');
                setViewEntryId(null);
                setPreselectedSectionId(null);
                setIsChatSidebarOpen(true);
              }} 
              isVisible={true}
              targetUserId={profileData?.id}
              targetUserProfile={profileData}
              timelineSections={timelineSections}
              sectionsLoading={isLoading}
              timelineError={!!error}
              hasTimelineSetup={!!profileData?.birthdate}
            />
        </div>
        </div>
      </AppShell>
    );
  }

  // Safely extract display name based on available data and authorization
  const displayName = isOwnProfile
    ? (user?.user_metadata?.firstName && user?.user_metadata?.lastName
        ? `${user.user_metadata.firstName} ${user.user_metadata.lastName}`
        : t('myTimeline', 'My Timeline'))
    : (profileData && profileData.first_name && profileData.last_name
        ? `${profileData.first_name} ${profileData.last_name}`
        : username || 'Unknown User');

  const formattedBirthdate = profileData?.birthdate 
    ? new Date(profileData.birthdate).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'de-DE', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
      }) 
    : '';

  // Get zodiac display name based on current language
  const zodiacDisplayName = getZodiacDisplayName(zodiacData, i18n.language);

  return (
    <AppShell
      showHeader={!isChatSidebarOpen}
      showNavigation={false}
      backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      headerContent={timelineHeader}
      removeBottomPadding={isChatSidebarOpen}
    >
      {/* Main Timeline Content Container with conditional padding */}
      <div className={`relative h-full ${isChatSidebarOpen ? 'md:pr-[400px]' : ''}`}>
        {/* Timeline Content */}
        <div className="h-full overflow-y-auto scrollbar-hide">
          <div className={`py-8 ${isChatSidebarOpen ? 'px-0 md:px-0' : ''}`}>
            <div className={`container mx-auto ${isChatSidebarOpen ? 'px-2 md:px-4' : ''}`}>
              {/* Timeline Header */}
              <div className="timeline-header-section max-w-2xl mx-auto mb-12 text-center">
                <h1 className="text-4xl font-bold text-uplivion-blue mb-2">{t('title')}</h1>
                <p className="text-xl text-gray-600">{t('of')}</p>
                <h2 className="text-3xl font-bold text-uplivion-blue mb-1">{displayName}</h2>
                {formattedBirthdate && (
                  <>
                    <p className="text-lg">{formattedBirthdate}</p>
                    {zodiacDisplayName && (
                      <p className="text-gray-500">{zodiacDisplayName}</p>
                    )}
                  </>
                )}
                {/* Debug reassignment indicator */}
                {isReassigning && isOwnProfile && (
                  <p className="text-sm text-orange-500 mt-2">
                    Reassigning timeline entries...
                  </p>
                )}
              </div>
            </div>
            
            <div className="timeline-sections-container relative">
              {sections.map((section, index) => (
                <TimelineSection 
                  key={section.id} 
                  section={{
                    ...section,
                    isExpanded: section.isExpanded
                  }} 
                  entries={entries?.filter(entry => entry.profile_timeline_section_id === section.id) || []} 
                  onToggleExpand={toggleSectionExpand} 
                  onCreateMemory={handleCreateMemoryForSection}
                  index={index}
                  timelineOwnerId={profileData?.id}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Chat Sidebar - Fixed positioning as overlay/sidebar */}
        {isChatSidebarOpen && (
          <div className="timeline-sidebar fixed inset-0 z-50 md:absolute md:right-0 md:top-0 md:bottom-0 md:w-[400px] md:inset-auto">
            <TimelineChatSidebar 
              className="h-full"
              isOpen={isChatSidebarOpen} 
              onClose={handleCloseSidebar} 
              onMemoryCreated={handleMemoryCreated}
              targetUserId={profileData?.id}
              targetUserProfile={profileData}
              timelineSections={timelineSections}
              sectionsLoading={isLoading}
              timelineError={!!error}
              mode={sidebarMode}
              viewEntryId={viewEntryId}
              preselectedSectionId={preselectedSectionId}
            />
          </div>
        )}

        {/* Timeline Footer Navigation - Only show when sidebar is closed */}
        {!isChatSidebarOpen && (
          <div className="fixed bottom-0 left-0 right-0 z-40">
            <TimelineNavigation 
              onMemoryCreated={handleMemoryCreated} 
              onOpenSidebar={() => {
                setSidebarMode('create');
                setViewEntryId(null);
                setPreselectedSectionId(null);
                setIsChatSidebarOpen(true);
              }} 
              isVisible={true}
              targetUserId={profileData?.id}
              targetUserProfile={profileData}
              timelineSections={timelineSections}
              sectionsLoading={isLoading}
              timelineError={!!error}
              hasTimelineSetup={!!profileData?.birthdate}
            />
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default Timeline;
